import Vue from "vue";
import { mapState } from "vuex";
import NotificationService from "../../../services/NotificationService";
import TranslationService from "../../../services/TranslationService";
import AddressSelect from "./AddressSelect";

export default Vue.component("shipping-address-select", {

    components:
    {
        AddressSelect
    },

    template: `
        <address-select
            ref:shipping-address-select
            template="#vue-address-select"
            @address-changed="addressChanged"
            address-type="2"
            :optional-address-fields="optionalAddressFields"
            :required-address-fields="requiredAddressFields"
            :default-salutation="defaultSalutation"
            :padding-classes="paddingClasses"
            :padding-inline-styles="paddingInlineStyles"
            :is-invalid-shipping-country="isInvalidShippingCountry"
            data-testing="delivery-address-select"
            :email="email">
        </address-select>
    `,

    props:
    {
        optionalAddressFields:
        {
            type: Object,
            default: () =>
            {
                return {};
            }
        },
        requiredAddressFields:
        {
            type: Object,
            default: () =>
            {
                return {};
            }
        },
        defaultSalutation:
        {
            type: String,
            default: App.config.addresses.defaultSalutation
        },
        hasAnyPostOfficePreset:
        {
            type: Boolean,
            default: false
        },
        hasAnyParcelBoxPreset:
        {
            type: Boolean,
            default: false
        },
        paddingClasses:
        {
            type: String,
            default: null
        },
        paddingInlineStyles:
        {
            type: String,
            default: null
        },
        email: String
    },

    data()
    {
        return {
            isInvalidShippingCountry: false
        };
    },

    computed:
    {
        /**
         * Country the order is actually shipped to.
         * Delivery address -99 means "same as billing address".
         */
        deliveryCountryId()
        {
            return Number(this.deliveryAddress?.id) === -99
                ? this.billingAddress?.countryId
                : this.deliveryAddress?.countryId;
        },

        ...mapState({
            shippingCountryList: state => state.localization.shippingCountries,
            billingAddress: state => state.address.billingAddress,
            billingAddressId: state => state.address.billingAddressId,
            deliveryAddress: state => state.address.deliveryAddress,
            deliveryAddressId: state => state.address.deliveryAddressId
        })
    },

    created()
    {
        this.$store.commit("setDeliveryAddressValidator", this.validate);
    },

    mounted()
    {
        if (App.templateType === "my-account")
        {
            if (this.hasAnyParcelBoxPreset)
            {
                this.$store.commit("setParcelBoxAvailability", true);
            }
            if (this.hasAnyPostOfficePreset)
            {
                this.$store.commit("setPostOfficeAvailability", true);
            }
        }

        this.checkDeliveryAddressError();
    },

    methods:
    {
        /**
         * Update the delivery address
         * @param selectedAddress
         */
        addressChanged(selectedAddress)
        {
            this.$store.dispatch("selectAddress", { selectedAddress, addressType: "2" })
                .then(
                    response =>
                    {
                        document.dispatchEvent(new CustomEvent("afterDeliveryAddressChanged", { detail: this.deliveryAddressId }));
                        this.checkDeliveryAddressError();
                    },
                    error =>
                    {

                    }
                );
        },

        /**
         * Compare numerically, the country id may arrive as a string from the address api.
         * @param countryId
         * @returns boolean
         */
        isShippingCountry(countryId)
        {
            return (this.shippingCountryList || []).some((country) => Number(country.id) === Number(countryId));
        },

        /**
         * Block the checkout if the delivery country is not part of the active shipping countries.
         * Registered as delivery address validator, called by PlaceOrder.
         */
        validate()
        {
            const countryId = this.deliveryCountryId;

            // Without a selected address the invoice address validator already blocks the checkout
            if (!countryId)
            {
                this.$store.commit("setDeliveryAddressShowError", false);
                return;
            }

            const isAllowedForShipping = this.isShippingCountry(countryId);

            this.$store.commit("setDeliveryAddressShowError", !isAllowedForShipping);
            this.isInvalidShippingCountry = !isAllowedForShipping;

            if (!isAllowedForShipping)
            {
                NotificationService.error(
                    TranslationService.translate("Ceres::Template.checkoutInvalidShippingCountry")
                );
            }
        },

        /**
         * Flag the delivery address if its country is not part of the active shipping countries.
         */
        checkDeliveryAddressError()
        {
            const countryId = this.deliveryCountryId;

            if (countryId)
            {
                this.isInvalidShippingCountry = !this.isShippingCountry(countryId);
            }
        }
    },

    watch:
    {
        billingAddress()
        {
            // if a delivery address exists do not take into account the billingAddress
            if (Number(this.deliveryAddressId) === -99)
            {
                this.checkDeliveryAddressError();
            }
        },

        deliveryAddress()
        {
            this.checkDeliveryAddressError();
        }
    }
});
